import React, { useState, useEffect, useCallback } from 'react';
import { FiMail, FiCheckCircle, FiLock, FiRefreshCw, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';

const cfg = () => {
  const token = localStorage.getItem('authToken');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// Owner/Admin panel: connect each team member's company mailbox in one place.
// These are company accounts the owner administers, so this is legitimate — but the
// member's unmatched list still stays private to them.
export default function TeamEmailConnect() {
  const [rows, setRows] = useState(null);
  const [pw, setPw] = useState({});          // memberId -> typed password
  const [busy, setBusy] = useState(null);    // memberId currently connecting
  const [msg, setMsg] = useState({});        // memberId -> result message
  const [open, setOpen] = useState(null);    // null = decide from data on first load

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/team/email-status`, cfg());
      const data = res.data || [];
      setRows(data);
      // Decide the default ONCE: collapsed when everyone's already connected (it's a
      // setup panel, not something to stare at), expanded if anyone still needs doing.
      setOpen(prev => {
        if (prev !== null) return prev;              // respect a manual toggle
        const eligible = data.filter(r => r.eligible);
        const done = eligible.filter(r => r.connected).length;
        return !(eligible.length > 0 && done === eligible.length);
      });
    } catch (e) { setRows([]); }   // hides for non-admins (endpoint 403s)
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!rows || rows.length === 0) return null;

  const connect = async (m) => {
    const password = (pw[m.id] || '').trim();
    if (!password) return;
    setBusy(m.id);
    setMsg({ ...msg, [m.id]: '' });
    try {
      const res = await axios.post(`${API_URL}/team/${m.id}/email-connect`, { password }, cfg());
      setMsg({ ...msg, [m.id]: res.data.message });
      setPw({ ...pw, [m.id]: '' });
      await load();
    } catch (e) {
      setMsg({ ...msg, [m.id]: e.response?.data?.error || 'Could not connect.' });
    } finally { setBusy(null); }
  };

  const disconnect = async (m) => {
    if (!window.confirm(`Disconnect ${m.name}'s mailbox? Email already on jobs stays put.`)) return;
    try {
      await axios.post(`${API_URL}/team/${m.id}/email-disconnect`, {}, cfg());
      await load();
    } catch (e) { setMsg({ ...msg, [m.id]: 'Could not disconnect.' }); }
  };

  const connectedCount = rows.filter(r => r.connected).length;
  const eligible = rows.filter(r => r.eligible);

  const allDone = eligible.length > 0 && connectedCount === eligible.length;

  return (
    <div style={{ backgroundColor: 'white', borderRadius: 16, padding: open ? 20 : '14px 20px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none',
          border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
        }}
      >
        {open ? <FiChevronDown size={16} color="#94a3b8" /> : <FiChevronRight size={16} color="#94a3b8" />}
        <FiMail size={16} color="#333" />
        <span style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>Team Email Connections</span>
        <span style={{
          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 12, fontWeight: 600,
          color: allDone ? '#166534' : '#92400e',
          background: allDone ? '#dcfce7' : '#fef3c7',
          padding: '3px 9px', borderRadius: 10,
        }}>
          {allDone && <FiCheckCircle size={12} />}
          {connectedCount} of {eligible.length} connected
        </span>
      </button>

      {open && (
        <>
          <p style={{ margin: '12px 0 14px', fontSize: 12.5, color: '#9ca3af', lineHeight: 1.5 }}>
            Connect each person's company mailbox so client email files onto jobs automatically.
            Passwords are verified before saving and stored encrypted. <strong>This does not give you
            their inbox</strong> — anything we can't match to a job stays private to them.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(m => (
          <div key={m.id} style={{
            border: '1px solid #e5e7eb', borderRadius: 10, padding: 10,
            background: m.connected ? '#f0fdf4' : 'white',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {m.name}
                  <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}> · {m.role}</span>
                </div>
                <div style={{ fontSize: 12.5, color: '#64748b' }}>{m.email || <em>no email on profile</em>}</div>
              </div>

              {!m.eligible ? (
                <span style={{ fontSize: 11.5, color: '#92400e', background: '#fef3c7', padding: '3px 8px', borderRadius: 6 }}>
                  not a company address
                </span>
              ) : m.connected ? (
                <>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#166534', fontWeight: 600 }}>
                    <FiCheckCircle size={13} /> Connected
                  </span>
                  {m.last_result && m.last_result.startsWith('error') && (
                    <span title={m.last_result} style={{ fontSize: 11, color: '#b91c1c' }}>sync failing</span>
                  )}
                  <button onClick={() => disconnect(m)}
                    style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                    disconnect
                  </button>
                </>
              ) : (
                <>
                  <div style={{ position: 'relative' }}>
                    <FiLock size={11} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1' }} />
                    <input
                      type="password"
                      value={pw[m.id] || ''}
                      onChange={(e) => setPw({ ...pw, [m.id]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') connect(m); }}
                      placeholder="mailbox password"
                      autoComplete="new-password"
                      style={{ padding: '7px 8px 7px 24px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, width: 170 }}
                    />
                  </div>
                  <button
                    onClick={() => connect(m)}
                    disabled={busy === m.id || !(pw[m.id] || '').trim()}
                    style={{
                      padding: '7px 12px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 12.5,
                      background: (pw[m.id] || '').trim() ? '#14532d' : '#e5e7eb',
                      color: (pw[m.id] || '').trim() ? 'white' : '#9ca3af',
                      cursor: (pw[m.id] || '').trim() ? 'pointer' : 'default',
                    }}>
                    {busy === m.id ? <><FiRefreshCw size={11} /> Checking…</> : 'Connect'}
                  </button>
                </>
              )}
            </div>
            {msg[m.id] && (
              <div style={{ fontSize: 12, marginTop: 6, color: msg[m.id].includes('connected') ? '#166534' : '#b91c1c' }}>
                {msg[m.id]}
              </div>
            )}
          </div>
        ))}
          </div>
        </>
      )}
    </div>
  );
}
